/*
 * Audiocolour Tap Tempo — interface.
 * Ordinary deferred script. Requires audiocolour-tap-tempo-core.js to be loaded
 * first (it publishes globalThis.AudiocolourTapTempoCore). All calculations,
 * tap analysis and BPM validation come from the core; this file only handles
 * events, state and rendering. One instance per [data-ac-tap] root.
 */
(function () {
  "use strict";

  // Guard against the script being included twice (e.g. two sections on a page).
  if (window.AudiocolourTapTempo) return;

  var COPY_FEEDBACK_MS = 1500;
  var HIT_FEEDBACK_MS = 120;
  var ANNOUNCE_AFTER_TAPS_MS = 1200;
  var CLICK_SUPPRESS_MS = 5000;
  var MAX_STORED_TAPS = 128;
  var TRIMMED_TAPS = 64;
  var CONSISTENCY_LEVELS = { Inconsistent: 1, Uneven: 2, Steady: 3, Locked: 4 };
  var MESSAGES = {
    empty: "Enter a tempo between 20 and 300 BPM.",
    range: "Tempo must be between 20 and 300 BPM.",
    number: "Enter a number, e.g. 120 or 128.5.",
    step: "Use up to one decimal place, e.g. 128.5.",
    bars: "Enter a whole number of bars from 1 to 999."
  };
  var MINUS = "−";

  var instances = new WeakMap();

  function formatBpm(bpm) {
    return bpm.toFixed(1);
  }

  function canonicalizeBpm(bpm) {
    var tenths = bpm * 10;
    return Math.round(tenths + Number.EPSILON * Math.abs(tenths) * 4) / 10;
  }

  function isActivationKey(event) {
    return event.key === " " || event.key === "Enter" || event.key === "Spacebar";
  }

  function multiplierText(exponent) {
    if (exponent === 0) return "";
    var factor = Math.pow(2, Math.abs(exponent));
    return (exponent < 0 ? "1/" + factor : String(factor)) + "× applied to taps";
  }

  function createInstance(root, core) {
    function part(name) { return root.querySelector("[data-ac-tap-" + name + "]"); }

    var els = {
      pad: part("pad"),
      input: part("input"),
      error: part("error"),
      count: part("count"),
      meter: part("meter"),
      consistency: part("consistency"),
      half: part("half"),
      double: part("double"),
      copy: part("copy"),
      reset: part("reset"),
      multiplier: part("multiplier"),
      status: part("status"),
      project: part("project"),
      projectError: part("project-error"),
      sampleBpm: part("sample-bpm"),
      matchSummary: part("match-summary"),
      matchChange: part("match-change"),
      matchDuration: part("match-duration"),
      signature: part("signature"),
      bars: part("bars"),
      barsError: part("bars-error"),
      loopSummary: part("loop-summary"),
      loopSeconds: part("loop-seconds"),
      loopMs: part("loop-ms"),
      delayTable: part("delay-table"),
      delayPrompt: part("delay-prompt")
    };
    for (var key in els) {
      if (!els[key]) return null;
    }
    var matchPanel = els.matchSummary.closest(".ac-tap-tool");
    var loopPanel = els.loopSummary.closest(".ac-tap-tool");
    var delayButtons = Array.prototype.slice.call(root.querySelectorAll("[data-ac-tap-delay]"));

    var controller = new AbortController();
    var timers = { hit: null, announceAfterTaps: null, announceWrite: null, copy: null, releaseClick: null };
    var state = {
      timestamps: [],
      analysis: null,
      factorExponent: 0,
      source: null, // null | "tap" | "manual"
      bpm: null, // canonical BPM used by every dependent calculation
      projectBpm: null, // validated Project BPM for Match this sample
      bars: null, // validated bar count for Loop length
      suppressNextClick: false,
      suppressClickUntil: 0,
      copySequence: 0,
      copyButton: null // the one button currently showing copy feedback
    };

    function on(target, type, handler) {
      target.addEventListener(type, handler, { signal: controller.signal });
    }

    function clearTimer(name) {
      if (timers[name] !== null) {
        window.clearTimeout(timers[name]);
        timers[name] = null;
      }
    }

    function announce(message) {
      clearTimer("announceWrite");
      els.status.textContent = "";
      timers.announceWrite = window.setTimeout(function () {
        els.status.textContent = message;
        timers.announceWrite = null;
      }, 20);
    }

    function setFieldError(input, errorEl, message, invalid) {
      errorEl.textContent = message || "";
      errorEl.hidden = !message;
      if (invalid) input.setAttribute("aria-invalid", "true");
      else input.removeAttribute("aria-invalid");
    }

    function setError(message, invalid) {
      setFieldError(els.input, els.error, message, invalid);
    }

    function setDisabled(button, disabled) {
      button.setAttribute("aria-disabled", disabled ? "true" : "false");
    }

    function isDisabled(button) {
      return button.getAttribute("aria-disabled") === "true";
    }

    // Applies the half/double multiplier to a measured BPM using the core.
    function scaleMeasured(bpm, exponent) {
      var result = { ok: true, bpm: bpm };
      for (var i = 0; i < Math.abs(exponent) && result.ok; i += 1) {
        result = exponent < 0 ? core.halfTime(result.bpm) : core.doubleTime(result.bpm);
      }
      return result.ok ? canonicalizeBpm(result.bpm) : null;
    }

    function render(syncInput) {
      var hasBpm = state.bpm !== null;

      if (syncInput) els.input.value = hasBpm ? formatBpm(state.bpm) : "";

      var dataState = "idle";
      if (state.source === "tap") dataState = "measuring";
      else if (state.source === "manual") dataState = hasBpm ? "manual" : "invalid";
      root.setAttribute("data-state", dataState);

      var label = "Tap to start";
      var level = 0;
      if (state.source === "manual") {
        label = "Manual tempo";
      } else if (state.source === "tap" && state.analysis) {
        label = state.analysis.consistencyLabel;
        level = CONSISTENCY_LEVELS[label] || 0;
      }
      els.consistency.textContent = label;
      els.meter.setAttribute("data-level", String(level));

      if (state.source === "manual") els.count.textContent = "—";
      else els.count.textContent = String(state.timestamps.length);

      var multiplier = state.source === "tap" ? multiplierText(state.factorExponent) : "";
      els.multiplier.textContent = multiplier;
      els.multiplier.hidden = !multiplier;

      setDisabled(els.half, !hasBpm || !core.halfTime(state.bpm).ok);
      setDisabled(els.double, !hasBpm || !core.doubleTime(state.bpm).ok);
      setDisabled(els.copy, !hasBpm);

      renderDependents();
    }

    // ---- Dependent panels (all values come from the core) -------------------

    function renderDependents() {
      renderMatch();
      renderLoop();
      renderDelays();
    }

    function setResult(panel, result) {
      panel.setAttribute("data-result", result);
    }

    function renderMatch() {
      var sample = state.bpm;
      els.sampleBpm.textContent = sample === null ? "—" : formatBpm(sample) + " BPM";

      var summary;
      var change = "—";
      var duration = "—";
      var result = "invalid";

      if (sample === null) {
        summary = "Tap or enter a tempo above to set the sample BPM.";
        result = "empty";
      } else if (state.projectBpm === null) {
        summary = "Enter a project BPM from 20 to 300 to compare.";
      } else {
        var match = core.matchSample(sample, state.projectBpm);
        if (!match.ok) {
          summary = match.error.message;
        } else {
          var percent = match.tempoChangePercent;
          var magnitude = Math.abs(percent).toFixed(2);
          if (percent === 0) {
            summary = "The tempos already match. No change needed.";
            change = "0.00%";
          } else if (percent > 0) {
            summary = "Speed the sample up by " + magnitude + "% to match the project.";
            change = "+" + magnitude + "%";
          } else {
            summary = "Slow the sample down by " + magnitude + "% to match the project.";
            change = MINUS + magnitude + "%";
          }
          duration = (match.durationRatio * 100).toFixed(2) + "%";
          result = "ready";
        }
      }

      els.matchSummary.textContent = summary;
      els.matchChange.textContent = change;
      els.matchDuration.textContent = duration;
      setResult(matchPanel, result);
    }

    function selectedSignature() {
      var parts = els.signature.value.split("/");
      return { numerator: Number(parts[0]), denominator: Number(parts[1]), label: els.signature.value };
    }

    function renderLoop() {
      var summary;
      var seconds = "—";
      var milliseconds = "—";
      var result = "invalid";
      var signature = selectedSignature();

      if (state.bpm === null) {
        summary = "Tap or enter a tempo above to calculate loop length.";
        result = "empty";
      } else if (state.bars === null) {
        summary = MESSAGES.bars;
      } else {
        var loop = core.loopDuration(state.bpm, signature.numerator, signature.denominator, state.bars);
        if (!loop.ok) {
          summary = loop.error.message;
        } else {
          summary = state.bars + (state.bars === 1 ? " bar" : " bars") + " of " + signature.label +
            " at " + formatBpm(state.bpm) + " BPM";
          seconds = loop.durationSeconds.toFixed(3) + " s";
          milliseconds = (loop.durationSeconds * 1000).toFixed(2) + " ms";
          result = "ready";
        }
      }

      els.loopSummary.textContent = summary;
      els.loopSeconds.textContent = seconds;
      els.loopMs.textContent = milliseconds;
      setResult(loopPanel, result);
    }

    function renderDelays() {
      var hasBpm = state.bpm !== null;
      var byDenominator = {};
      els.delayPrompt.hidden = hasBpm;

      delayButtons.forEach(function (button) {
        var denominator = Number(button.getAttribute("data-denominator"));
        var variant = button.getAttribute("data-variant");
        var noteName = button.getAttribute("data-note-name");
        var variantName = button.getAttribute("data-variant-name");
        var valueEl = button.querySelector("[data-ac-tap-delay-value]");
        var values = null;

        if (hasBpm) {
          if (!byDenominator[denominator]) byDenominator[denominator] = core.delayNoteValues(state.bpm, denominator);
          values = byDenominator[denominator].ok ? byDenominator[denominator] : null;
        }

        var ms = values ? values[variant + "Ms"] : null;
        if (typeof ms === "number" && Number.isFinite(ms)) {
          var text = ms.toFixed(2);
          valueEl.textContent = text;
          button.setAttribute("data-value", text);
          button.setAttribute("aria-label", "Copy " + variantName.toLowerCase() + " " + noteName + " note delay, " + text + " milliseconds");
          setDisabled(button, false);
        } else {
          valueEl.textContent = "—";
          button.removeAttribute("data-value");
          button.setAttribute("aria-label", variantName + " " + noteName + " note delay, no value");
          setDisabled(button, true);
        }
      });
    }

    // ---- Tapping -----------------------------------------------------------

    function flashPad() {
      clearTimer("hit");
      els.pad.setAttribute("data-hit", "");
      timers.hit = window.setTimeout(function () {
        els.pad.removeAttribute("data-hit");
        timers.hit = null;
      }, HIT_FEEDBACK_MS);
    }

    function scheduleTapAnnouncement() {
      clearTimer("announceAfterTaps");
      timers.announceAfterTaps = window.setTimeout(function () {
        timers.announceAfterTaps = null;
        if (state.source !== "tap" || state.bpm === null || !state.analysis || !state.analysis.estimateAvailable) return;
        var label = state.analysis.consistencyLabel;
        announce("Tempo " + formatBpm(state.bpm) + " BPM" + (CONSISTENCY_LEVELS[label] ? ". " + label + "." : "."));
      }, ANNOUNCE_AFTER_TAPS_MS);
    }

    function registerTap(now) {
      if (state.source !== "tap") {
        // Starting a sequence after idle or a manual tempo.
        state.timestamps = [];
        state.factorExponent = 0;
      }

      var last = state.timestamps[state.timestamps.length - 1];
      if (typeof last === "number" && now <= last) return; // the core requires strictly increasing times

      state.timestamps.push(now);
      if (state.timestamps.length > MAX_STORED_TAPS) {
        state.timestamps = state.timestamps.slice(-TRIMMED_TAPS);
      }

      var analysis = core.analyzeTaps(state.timestamps);
      if (!analysis.ok || analysis.event === "pause-reset") {
        // A pause reset starts a new sequence at this tap and drops the multiplier.
        state.timestamps = [now];
        state.factorExponent = 0;
        state.bpm = null;
        analysis = core.analyzeTaps(state.timestamps);
      }

      state.source = "tap";
      state.analysis = analysis;

      if (analysis.estimateAvailable) {
        var scaled = scaleMeasured(analysis.bpm, state.factorExponent);
        if (scaled === null) {
          state.factorExponent = 0;
          scaled = analysis.bpm;
          announce("Half or double time removed: the result would be outside 20 to 300 BPM.");
        }
        state.bpm = scaled;
      }
      // Without an estimate yet, the previous tempo stays visible until the new one is available.

      setError("", false);
      flashPad();
      render(true);
      scheduleTapAnnouncement();
    }

    function suppressClick() {
      state.suppressNextClick = true;
      state.suppressClickUntil = performance.now() + CLICK_SUPPRESS_MS;
    }

    function releaseClickSoon() {
      // Any click generated by the same key press arrives before this timeout runs.
      clearTimer("releaseClick");
      timers.releaseClick = window.setTimeout(function () {
        state.suppressNextClick = false;
        timers.releaseClick = null;
      }, 0);
    }

    on(els.pad, "pointerdown", function (event) {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      suppressClick();
      registerTap(performance.now());
    });

    on(els.pad, "pointercancel", function () {
      state.suppressNextClick = false;
    });

    on(els.pad, "keydown", function (event) {
      if (!isActivationKey(event)) return;
      event.preventDefault(); // stops page scrolling and native activation
      if (event.repeat) return;
      suppressClick();
      registerTap(performance.now());
      if (event.key === "Enter") releaseClickSoon();
    });

    on(els.pad, "keyup", function (event) {
      if (!isActivationKey(event)) return;
      event.preventDefault();
      releaseClickSoon();
    });

    // Screen readers often activate buttons with a click alone, without
    // pointer or key events. Clicks that follow a handled press are ignored.
    on(els.pad, "click", function () {
      var now = performance.now();
      if (state.suppressNextClick && now < state.suppressClickUntil) {
        state.suppressNextClick = false;
        return;
      }
      state.suppressNextClick = false;
      registerTap(now);
    });

    // ---- Manual entry --------------------------------------------------------

    function readInput(eventType) {
      return readBpmField(els.input, eventType);
    }

    // Shared validation for the main tempo and the Project BPM fields.
    function readBpmField(input, eventType) {
      var committed = eventType === "change";

      if (input.validity.badInput) return { bpm: null, message: MESSAGES.number, invalid: true };
      if (input.value.trim() === "") {
        return { bpm: null, message: committed ? MESSAGES.empty : "", invalid: committed };
      }

      var value = input.valueAsNumber;
      if (!Number.isFinite(value)) return { bpm: null, message: MESSAGES.number, invalid: true };

      var validation = core.validateBpm(value);
      if (!validation.ok) {
        // Below-minimum values may still be mid-typing ("1" on the way to "120").
        if (value < 20 && !committed) return { bpm: null, message: "", invalid: false };
        return { bpm: null, message: MESSAGES.range, invalid: true };
      }
      if (input.validity.stepMismatch) return { bpm: null, message: MESSAGES.step, invalid: true };

      return { bpm: validation.bpm, message: "", invalid: false };
    }

    function handleManual(eventType) {
      // Manual entry ends the measured sequence and uses the entered tempo directly.
      clearTimer("announceAfterTaps");
      state.timestamps = [];
      state.analysis = null;
      state.factorExponent = 0;
      state.source = "manual";

      var result = readInput(eventType);
      state.bpm = result.bpm;
      setError(result.message, result.invalid);

      var normalise = eventType === "change" && result.bpm !== null;
      render(normalise);
    }

    on(els.input, "input", function () { handleManual("input"); });
    on(els.input, "change", function () { handleManual("change"); });

    // ---- Match this sample and Loop length inputs ---------------------------

    function handleProject(eventType) {
      var result = readBpmField(els.project, eventType);
      state.projectBpm = result.bpm;
      setFieldError(els.project, els.projectError, result.message, result.invalid);
      if (eventType === "change" && result.bpm !== null) els.project.value = formatBpm(result.bpm);
      renderMatch();
    }

    function readBars(eventType) {
      var input = els.bars;
      var committed = eventType === "change";
      if (input.value.trim() === "" && !input.validity.badInput) {
        return { bars: null, message: committed ? MESSAGES.bars : "", invalid: committed };
      }
      var value = input.valueAsNumber;
      var validity = input.validity;
      if (validity.badInput || validity.rangeUnderflow || validity.rangeOverflow || validity.stepMismatch || !Number.isFinite(value)) {
        return { bars: null, message: MESSAGES.bars, invalid: true };
      }
      return { bars: value, message: "", invalid: false };
    }

    function handleBars(eventType) {
      var result = readBars(eventType);
      state.bars = result.bars;
      setFieldError(els.bars, els.barsError, result.message, result.invalid);
      renderLoop();
    }

    on(els.project, "input", function () { handleProject("input"); });
    on(els.project, "change", function () { handleProject("change"); });
    on(els.bars, "input", function () { handleBars("input"); });
    on(els.bars, "change", function () { handleBars("change"); });
    on(els.signature, "change", renderLoop);

    // ---- Half / double -----------------------------------------------------

    function scaleCurrent(direction) {
      var button = direction < 0 ? els.half : els.double;
      if (isDisabled(button) || state.bpm === null) return;

      if (state.source === "tap" && state.analysis && state.analysis.estimateAvailable) {
        var nextExponent = state.factorExponent + direction;
        var measuredBpm = scaleMeasured(state.analysis.bpm, nextExponent);
        if (measuredBpm === null) return;
        state.factorExponent = nextExponent;
        state.bpm = measuredBpm;
      } else {
        var result = direction < 0 ? core.halfTime(state.bpm) : core.doubleTime(state.bpm);
        if (!result.ok) return;
        state.bpm = canonicalizeBpm(result.bpm);
      }
      setError("", false);
      render(true);
      announce((direction < 0 ? "Half time: " : "Double time: ") + formatBpm(state.bpm) + " BPM.");
    }

    on(els.half, "click", function () { scaleCurrent(-1); });
    on(els.double, "click", function () { scaleCurrent(1); });

    // ---- Copy --------------------------------------------------------------

    function fallbackCopy(value) {
      var textarea = document.createElement("textarea");
      var previouslyFocused = document.activeElement;
      textarea.value = value;
      textarea.className = "ac-tap-copy-fallback";
      textarea.readOnly = true;
      var copied = false;
      try {
        root.appendChild(textarea);
        textarea.focus();
        textarea.setSelectionRange(0, textarea.value.length);
        copied = document.execCommand("copy");
      } catch (error) {
        copied = false;
      } finally {
        if (textarea.parentNode) textarea.parentNode.removeChild(textarea);
        if (previouslyFocused && typeof previouslyFocused.focus === "function") {
          try { previouslyFocused.focus(); } catch (error) { /* best effort */ }
        }
      }
      return copied;
    }

    function copyText(value) {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
        return navigator.clipboard.writeText(value).then(
          function () { return true; },
          function () { return fallbackCopy(value); }
        );
      }
      return Promise.resolve(fallbackCopy(value));
    }

    // Copy feedback is shown on one button at a time across the instance.
    function setCopyState(button, copyState) {
      var label = button.querySelector("[data-ac-tap-copy-text]");
      if (copyState) {
        button.setAttribute("data-copy-state", copyState);
        if (label) label.textContent = copyState === "copied" ? "Copied" : (button === els.copy ? "Copy failed" : "Failed");
      } else {
        button.removeAttribute("data-copy-state");
        if (label) label.textContent = label.getAttribute("data-idle-text");
      }
    }

    function clearCopyState() {
      clearTimer("copy");
      if (state.copyButton) setCopyState(state.copyButton, null);
      state.copyButton = null;
    }

    function copyFrom(button, value, successMessage) {
      state.copySequence += 1;
      var thisCopy = state.copySequence;
      clearCopyState();

      copyText(value).then(function (copied) {
        if (thisCopy !== state.copySequence || controller.signal.aborted) return;
        state.copyButton = button;
        setCopyState(button, copied ? "copied" : "error");
        announce(copied ? successMessage : "Couldn't copy " + value + ". Your browser blocked clipboard access.");
        timers.copy = window.setTimeout(function () {
          timers.copy = null;
          if (thisCopy === state.copySequence) clearCopyState();
        }, COPY_FEEDBACK_MS);
      });
    }

    on(els.copy, "click", function () {
      if (isDisabled(els.copy) || state.bpm === null) return;
      var value = formatBpm(state.bpm);
      copyFrom(els.copy, value, "Copied " + value + " BPM.");
    });

    on(els.delayTable, "click", function (event) {
      var button = event.target.closest("[data-ac-tap-delay]");
      if (!button || !els.delayTable.contains(button) || isDisabled(button)) return;
      var value = button.getAttribute("data-value");
      if (!value) return;
      copyFrom(button, value, "Copied " + value + " milliseconds for " +
        button.getAttribute("data-variant-name").toLowerCase() + " " + button.getAttribute("data-note-name") + " note.");
    });

    // ---- Reset -------------------------------------------------------------

    on(els.reset, "click", function () {
      clearTimer("announceAfterTaps");
      state.copySequence += 1;
      state.timestamps = [];
      state.analysis = null;
      state.factorExponent = 0;
      state.source = null;
      state.bpm = null;
      setError("", false);
      clearCopyState();
      render(true);
      announce("Tap tempo reset.");
    });

    // Read the defaults from the markup (Project BPM 120, 4 bars) before the first render.
    state.projectBpm = readBpmField(els.project, "initial").bpm;
    state.bars = readBars("initial").bars;
    render(true);

    return {
      destroy: function () {
        controller.abort();
        Object.keys(timers).forEach(clearTimer);
      }
    };
  }

  function rootsIn(scope) {
    if (!scope || typeof scope.querySelectorAll !== "function") return [];
    var roots = Array.prototype.slice.call(scope.querySelectorAll("[data-ac-tap]"));
    if (scope.matches && scope.matches("[data-ac-tap]")) roots.unshift(scope);
    return roots;
  }

  function init(scope) {
    var core = window.AudiocolourTapTempoCore;
    rootsIn(scope || document).forEach(function (root) {
      if (instances.has(root)) return;
      if (!core) {
        var notice = root.querySelector("[data-ac-tap-unavailable]");
        if (notice) notice.hidden = false;
        root.setAttribute("data-state", "unavailable");
        return;
      }
      var instance = createInstance(root, core);
      if (instance) instances.set(root, instance);
    });
  }

  function destroy(scope) {
    rootsIn(scope || document).forEach(function (root) {
      var instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  }

  window.AudiocolourTapTempo = Object.freeze({ init: init, destroy: destroy });

  // Theme editor: sections are re-rendered in place.
  document.addEventListener("shopify:section:unload", function (event) { destroy(event.target); });
  document.addEventListener("shopify:section:load", function (event) { init(event.target); });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { init(document); });
  } else {
    init(document);
  }
}());
